import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      const response = await fetch('https://readdy.ai/api/form/d5pm1e0tu5df1co4cvqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-navy-dark text-gray-300">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">

          {/* Brand */}
          <div>
            <div className="flex items-center space-x-3 mb-5">
              <div className="w-9 h-9 flex items-center justify-center bg-orange-brand rounded-lg">
                <i className="ri-building-2-line text-white text-lg"></i>
              </div>
              <span className="text-xl font-heading font-extrabold text-white">
                Demo<span className="text-orange-brand">Hub</span>
              </span>
            </div>
            <p className="text-sm font-sans leading-relaxed text-gray-400 mb-6">
              Melbourne's trusted demolition and site assessment platform. Fast quotes, licensed contractors, transparent pricing.
            </p>
            <div className="flex items-center space-x-3">
              {['ri-facebook-fill', 'ri-linkedin-fill', 'ri-instagram-fill', 'ri-twitter-x-fill'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/15 text-gray-400 hover:text-orange-brand hover:border-orange-brand/50 transition-colors cursor-pointer"
                >
                  <i className={`${icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-5">
              <a id="footer-services">Services</a>
            </h4>
            <ul className="space-y-3">
              {[
                'Residential Demolition',
                'Commercial Demolition',
                'Arborist Report',
                'Impact Assessment',
                'Land Assessment',
                'Soil / Geotechnical Test',
              ].map((s) => (
                <li key={s}>
                  <Link to="/services" className="text-sm font-sans text-gray-400 hover:text-orange-brand transition-colors">
                    {s}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-5">
              <a id="footer-company">Company</a>
            </h4>
            <ul className="space-y-3">
              {[
                { to: '/about', label: 'About Us' },
                { to: '/licensees', label: 'Join Our Network' },
                { to: '/quotations', label: 'Get a Quote' },
                { to: '/contact', label: 'Contact Us' },
                { to: '/dashboard', label: 'Client Login' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="text-sm font-sans text-gray-400 hover:text-orange-brand transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Newsletter */}
          <div>
            <h4 className="font-heading font-bold text-white text-sm uppercase tracking-wider mb-5">
              <a id="footer-contact">Get in Touch</a>
            </h4>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-phone-line text-orange-brand text-sm"></i>
                </div>
                <span className="text-sm font-sans text-gray-400">1800 DEMOHUB</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-mail-line text-orange-brand text-sm"></i>
                </div>
                <span className="text-sm font-sans text-gray-400">info@demohub.com.au</span>
              </li>
              <li className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-map-pin-line text-orange-brand text-sm"></i>
                </div>
                <span className="text-sm font-sans text-gray-400">Melbourne, VIC, Australia</span>
              </li>
            </ul>

            <form onSubmit={handleSubmit} data-readdy-form id="newsletter-footer" className="space-y-2">
              <p className="text-xs font-sans text-gray-500 uppercase tracking-wider mb-2 font-semibold">Newsletter</p>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm font-sans text-white placeholder-gray-500 focus:outline-none focus:border-orange-brand transition-colors"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-2.5 bg-orange-brand text-white rounded-lg text-sm font-heading font-bold hover:bg-orange-dark transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
              {submitStatus === 'success' && <p className="text-green-400 text-xs">Subscribed successfully!</p>}
              {submitStatus === 'error' && <p className="text-red-400 text-xs">Something went wrong. Try again.</p>}
            </form>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0">
          <p className="text-xs font-sans text-gray-500">
            © {new Date().getFullYear()} DemoHub. All rights reserved. ABN 00 000 000 000
          </p>
          <div className="flex items-center space-x-6">
            <a href="#" rel="nofollow" className="text-xs font-sans text-gray-500 hover:text-orange-brand transition-colors">Privacy Policy</a>
            <a href="#" rel="nofollow" className="text-xs font-sans text-gray-500 hover:text-orange-brand transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
